import React from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams, createSearchParams } from "react-router-dom";
import { Header } from "../../../components/header";
import { Button, Select, SearchBar } from "../../../components";
import { Loading } from "../../../components/loading";
import { EmptyList } from "../../../components/empty-list";
import { Table, TableHeader, TableBody, TableRow, TableCell } from "../../../components/tables";
import { SendIcon } from "../../../components/icons";
import { useProducts } from "../hooks";
import { cleanObject } from "../../../helpers/clean-object";

interface ISearchForm {
  search?: string;
  priceOnly?: string;
}

export const Products: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const form = useForm<ISearchForm>({
    mode: "all",
    defaultValues: {
      search: searchParams.get("search") ?? "",
      priceOnly: searchParams.get("priceOnly") ?? "",
    },
  });

  const onSubmit = () => {
    const values = form.getValues();
    navigate({
      pathname: "/products",
      search: `?${createSearchParams(cleanObject(values))}`,
    });
  };

  const search = searchParams.get("search");
  const priceOnly = searchParams.get("priceOnly");

  const where: any = {};
  if (search) where.search = search;
  if (priceOnly) where.priceOnly = priceOnly === "true";

  const limit = 25;
  const { data, loading, fetchMore } = useProducts({
    where,
    limit,
    offset: 0,
  });

  return (
    <>
      <Header
        title="Produits"
        subtitle="Liste des produits"
        buttons={[
          {
            actionText: "Nouveau produit",
            bgColor: "indigo",
            textColor: "white",
            link: `/product/create`,
            icon: <SendIcon />,
          },
        ]}
      />

      <div className="main-container">
        <SearchBar
          form={form}
          onSubmit={onSubmit}
          placeholder="Rechercher un produit..."
          advancedFilters={
            <Select label="Prix seul" className="w-full" {...form.register("priceOnly")}>
              <option value="">Tous</option>
              <option value="true">Oui</option>
              <option value="false">Non</option>
            </Select>
          }
        />

        <div className="p-4 mb-6 bg-white shadow rounded overflow-x-auto">
          {loading && !data ? (
            <Loading />
          ) : !data?.products?.results?.length ? (
            <EmptyList text="Aucun produit" />
          ) : (
            <Table>
              <TableHeader>
                <TableRow hover={false}>
                  <TableCell header>Nom</TableCell>
                  <TableCell header>Référence</TableCell>
                  <TableCell header align="right">
                    Dernier prix
                  </TableCell>
                  <TableCell header align="right">
                    Stock
                  </TableCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.products?.results?.map((product) => (
                  <TableRow
                    key={`product-${product.id}`}
                    onClick={() => navigate(`/product/${product.id}`)}
                  >
                    <TableCell>{product.name}</TableCell>
                    <TableCell>{product.reference}</TableCell>
                    <TableCell align="right">{product.lastPrice}</TableCell>
                    <TableCell align="right">{product.totalStock}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {data?.products?.hasMore && (
          <div>
            <Button
              disabled={loading}
              loading={loading}
              onClick={() => {
                fetchMore({
                  variables: {
                    offset: data?.products?.results?.length,
                    limit,
                    where,
                  },
                });
              }}
            >
              Plus
            </Button>
          </div>
        )}
      </div>
    </>
  );
};
